import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import CheckIcon from "@mui/icons-material/Check"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import CloseIcon from "@mui/icons-material/Close"
import { IconButton, List, ListItemButton } from "@mui/material"
import { FC, useEffect, useState } from "react"

import { Drawer } from "src/components/common/drawer"
import { create } from "src/helpers/bem"
import { Category, useCategories } from "src/states/shop/category"
import { useFilter } from "src/states/shop/filter"
import { TranslationMessages } from "src/translations"

import { getParentCategory, selectCategory } from "./shopCategoryFilter"
import styles from "./ShopOverviewCategorySheet.module.scss"

const bem = create(styles, "ShopOverviewCategorySheet")

export type ShopOverviewCategorySheetProps = {
  messages: TranslationMessages
  open: boolean
  onClose: () => void
}

/** Category selection for small screens, opens from the bottom. */
export const ShopOverviewCategorySheet: FC<ShopOverviewCategorySheetProps> = ({
  messages,
  open,
  onClose,
}) => {
  const m = messages.pages.shop.overview.header.category
  // Store values are read-only, the sheet only reads them
  const categories = useCategories().categories as Category[]
  const { filterBy } = useFilter()
  const active = filterBy?.category as Category | null | undefined

  // Category whose subcategories are shown, undefined = top level
  const [parent, setParent] = useState<Category>()

  // Open where the current selection is
  useEffect(() => {
    if (open) {
      setParent(getParentCategory(categories, active))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const select = (category: Category) => {
    selectCategory(category.id)
    onClose()
  }

  const renderItem = (
    category: Category,
    label: string,
    onClick: () => void,
    hasChildren = false,
  ) => {
    // A parent counts as active if one of its subcategories is selected
    const isActive =
      category.id === active?.id ||
      (hasChildren && active?.parentCategoryId === category.id)
    return (
      <ListItemButton
        key={`${category.id}-${label}`}
        className={bem("item", { "is-active": isActive })}
        selected={isActive}
        onClick={onClick}
      >
        <span className={bem("item__label")}>{label}</span>
        {hasChildren ? (
          <ChevronRightIcon className={bem("item__icon")} />
        ) : (
          isActive && <CheckIcon className={bem("item__icon")} />
        )}
      </ListItemButton>
    )
  }

  const children = (parent?.children ?? []) as Category[]

  return (
    // disablePortal: render inside the layout so the sheet gets the theme
    // font and color variables (a portal ends up outside of them)
    <Drawer
      disablePortal
      anchor="bottom"
      open={open}
      PaperProps={{ className: bem("paper") }}
      onClose={onClose}
      onOpen={() => undefined}
    >
      <div className={bem("header")}>
        {parent ? (
          <button
            className={bem("back")}
            type="button"
            onClick={() => setParent(undefined)}
          >
            <ArrowBackIosNewIcon fontSize="small" />
            {m.back}
          </button>
        ) : (
          <span className={bem("title")}>{m.title}</span>
        )}
        <IconButton aria-label={m.close} title={m.close} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <List className={bem("list")}>
        {parent
          ? [
              renderItem(parent, `${m.all} ${parent.name}`, () =>
                select(parent),
              ),
              ...children.map((child) =>
                renderItem(child, child.name ?? "", () => select(child)),
              ),
            ]
          : categories.map((category) =>
              category.children?.length
                ? renderItem(
                    category,
                    category.name ?? "",
                    () => setParent(category),
                    true,
                  )
                : renderItem(category, category.name ?? "", () =>
                    select(category),
                  ),
            )}
      </List>
    </Drawer>
  )
}
